<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  useWhatsAppOutboundCall,
  useOutboundCallMessageListener,
} from 'dashboard/composables/useWhatsAppOutboundCall';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { t } = useI18n();
const { initiateCall: initiateWhatsAppCall } = useWhatsAppOutboundCall();

// Setup message listener for outbound WhatsApp calls
useOutboundCallMessageListener();

const dialogRef = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentAccountId = useMapGetter('getCurrentAccountId');

const voiceInboxes = computed(() =>
  (inboxesList.value || []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.VOICE
  )
);

const whatsappInboxes = computed(() =>
  (inboxesList.value || []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.WHATSAPP
  )
);

// Combined list of all calling-capable inboxes
const callingInboxes = computed(() => [
  ...voiceInboxes.value,
  ...whatsappInboxes.value,
]);

const hasCallingInboxes = computed(() => callingInboxes.value.length > 0);

// Unified behavior: hide when no phone
const shouldRender = computed(() => hasCallingInboxes.value && !!props.phone);

const isInitiatingCall = computed(() => {
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const startTwilioCall = async inboxId => {
  if (isInitiatingCall.value) return;

  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.contactId,
      inboxId,
    });

    const { call_sid: callSid, conversation_id: conversationId } = response;

    // Add call to store immediately so widget shows
    store.dispatch('calls/setIncomingCall', {
      callSid,
      conversationId,
      inboxId,
      isOutbound: true,
      phoneNumber: props.phone,
      contactName: response?.contact?.name || props.phone,
      inboxName: voiceInboxes.value.find(i => i.id === inboxId)?.name,
    });

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
    navigateToConversation(conversationId);
  } catch (error) {
    const apiError = error?.message;
    useAlert(apiError || t('CONTACT_PANEL.CALL_FAILED'));
  }
};

const startWhatsAppCall = async inboxId => {
  if (isInitiatingCall.value) return;

  // Some countries forbid business-initiated calls, so a number can receive
  // calls while being unable to place them. Say so up front instead of firing a
  // request that can only fail.
  const inbox = whatsappInboxes.value.find(i => i.id === inboxId);
  if (!inbox?.provider_config?.callback_permission_enabled) {
    useAlert(t('CONTACT_PANEL.WHATSAPP_OUTBOUND_CALLS_UNAVAILABLE'));
    return;
  }

  // Get current conversation ID if we're in a conversation
  const currentConversationId = route.params.conversationId;

  console.log('[VoiceCallButton] Starting WhatsApp call with props:', {
    contactId: props.contactId,
    phone: props.phone,
    inboxId,
    conversationId: currentConversationId,
  });

  try {
    const { conversation_id: conversationId } = await initiateWhatsAppCall({
      contactId: props.contactId,
      inboxId,
      accountId: currentAccountId.value,
      conversationId: currentConversationId, // Pass current conversation if available
    });

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));

    // If we're already in the conversation, don't navigate
    if (
      currentConversationId &&
      currentConversationId === String(conversationId)
    ) {
      console.log(
        '[VoiceCallButton] Already in conversation, no navigation needed'
      );
      return;
    }

    // Wait 1.5 seconds for conversation to sync via ActionCable before navigating
    // This prevents 404 errors when navigating to newly created conversations
    setTimeout(() => {
      navigateToConversation(conversationId);
    }, 1500);
  } catch (error) {
    // Extract error message from API response (for call permission errors)
    const apiMessage = error?.response?.data?.message;
    const apiError = apiMessage || error?.message;
    useAlert(apiError || t('CONTACT_PANEL.CALL_FAILED'));
  }
};

const startCall = async inbox => {
  const inboxId = typeof inbox === 'object' ? inbox.id : inbox;
  const inboxObj = callingInboxes.value.find(i => i.id === inboxId);

  if (!inboxObj) {
    console.error('[VoiceCallButton] Inbox not found:', inboxId);
    return;
  }

  // Route to correct call type based on inbox channel
  if (inboxObj.channel_type === INBOX_TYPES.WHATSAPP) {
    await startWhatsAppCall(inboxId);
  } else if (inboxObj.channel_type === INBOX_TYPES.VOICE) {
    await startTwilioCall(inboxId);
  }
};

const isWhatsAppCallingEnabled = inbox => {
  if (inbox.channel_type !== INBOX_TYPES.WHATSAPP) return true;
  return inbox.provider_config?.calling_enabled === true;
};

const onClick = async () => {
  if (callingInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = callingInboxes.value;
  await startCall(inbox);
};

const onPickInbox = async inbox => {
  // Prevent calling for disabled WhatsApp channels
  if (!isWhatsAppCallingEnabled(inbox)) {
    return;
  }
  dialogRef.value?.close();
  await startCall(inbox);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isInitiatingCall"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && callingInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in callingInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg"
          :class="[
            isWhatsAppCallingEnabled(inbox)
              ? 'hover:bg-n-alpha-2 cursor-pointer'
              : 'opacity-60 cursor-not-allowed',
          ]"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span
              v-if="inbox.channel_type === 'Channel::Whatsapp'"
              class="i-ri-whatsapp-fill text-n-teal-9"
            />
            <span v-else class="i-ri-phone-fill text-n-slate-10" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
            <span
              v-if="
                inbox.channel_type === 'Channel::Whatsapp' &&
                isWhatsAppCallingEnabled(inbox)
              "
              class="text-xs text-n-teal-9 bg-n-teal-2 px-1.5 py-0.5 rounded"
            >
              WhatsApp
            </span>
            <span
              v-else-if="
                inbox.channel_type === 'Channel::Whatsapp' &&
                !isWhatsAppCallingEnabled(inbox)
              "
              class="text-xs text-n-ruby-9 bg-n-ruby-2 px-1.5 py-0.5 rounded"
            >
              Calling disabled
            </span>
            <span
              v-else
              class="text-xs text-n-slate-10 bg-n-slate-2 px-1.5 py-0.5 rounded"
            >
              Twilio
            </span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
